import { BUILDER_END, availableTo, earlyTasks, isReady, lockedCompleteness, phaseOfNode, phasesOf, planDisable, titleCase, type BuilderWho, type Hold, type ProcessStepDto, type ProjectProcess } from '@nsb/core';
import { dateWords, yearRule, type YearRule } from '../../days.ts';
import { bandOf, type BandFacts } from './bands.ts';
import { NOT_CONFIGURED } from './look.ts';
import { gateDone } from './passed.ts';
import { earlyFor, opensFrom, stepName, tileWords, type Standing, type TilePhase, type TileStep } from './model.ts';
import { heldWords } from '../process/holdWords.ts';
import { lateWords } from '../process/lateWords.ts';
import type { FindStep } from './find.ts';
import type { ProcessModel } from '../process/useProcess.ts';

/**
 * A job's process as NSB has it, as tiles (design study 12): its steps as the master draws them, where each stands at
 * its foot (states A). A step left out is a dot in the line where it would have been, its name crossed out, what came
 * after it drawn on from the dot (design study 18); clicked, it shows with + Add and the filing box (JobTree). Nothing the
 * job has not got is drawn: setting it up, a step is added from its phase's list, ticking it showing what it needs first
 * (design study 31, slice 2; Jason, 2026-10-29: working back "only makes sense if the dependencies are correct").
 */

const WHO_OF_PERFORMER: Record<string, BuilderWho> = { nsb: 'nsb', sub: 'sub', supplier: 'supplier', consultant: 'sub', owner: 'client' };
const WHO_OF_OWNER: Record<string, BuilderWho> = { nsb: 'nsb', client: 'client', sub: 'sub', arch: 'sub', vendor: 'supplier', authority: 'outside' };

/** What a step of a job's band is drawn from (the one band rule, bands.ts): the job's own performer, else its owner. */
function jobFacts(s: Pick<ProcessStepDto, 'kind' | 'owner' | 'trade' | 'performer' | 'key'>, start: string | null): BandFacts {
  if (s.key !== null && s.key === start) return { kind: 'start', who: null };
  if (s.key === 'project.archive') return { kind: 'end', who: null };
  if (s.kind === 'inspection') return { kind: 'work', who: 'outside', outside: 'Authority' };
  const who = s.performer ? (WHO_OF_PERFORMER[s.performer.kind] ?? null) : s.owner ? (WHO_OF_OWNER[s.owner] ?? null) : null;
  return { kind: s.kind, who, trade: s.trade };
}

/**
 * What a job's standing reads besides the step itself: what holds a phase's gate, the phases by name, and how its days
 * read their year (`YearRule`, L-25a: the job's `today`, and whether the job runs over more than one year, `yearRule`).
 */
export interface StandingContext extends YearRule {
  /** Each gate that closes a phase, with the steps of its phase not done it waits on by rule (core `phaseHolds`). */
  holds?: ReadonlyMap<string, readonly string[]>;
  /** What holds each step back, nearest first: every gate and inspection not passed, however far back (core `gateHolds`, L-17 B). */
  held?: ReadonlyMap<string, readonly Hold[]>;
  /** A phase by the tree's name for it. */
  phaseName?: (key: string) => string;
}

/** A deviation not confirmed (design study 21), in words: waiting on the client's answer, or declined by them. */
const unconfirmed = (s: Pick<ProcessStepDto, 'deviation'>): string | null =>
  !s.deviation || s.deviation.answer === 'confirmed' ? null : s.deviation.answer === 'waiting' ? 'waiting on the client' : 'declined by the client';

/**
 * Where a step of a job stands, and the words at its foot (study 12, states A); ready by the one rule (core `isReady`),
 * and, for the gate that closes a phase, the rest of its phase done (core `phaseHolds`, item 7). A deviation (design
 * study 21) says so: a step come late and not done is marked late (`deviation`), held while the client has not
 * confirmed it ("Late · waiting on the client", "Late · declined by the client"), else "Late · from" its own phase; a
 * gate marked done before the rest of its phase reads as waiting on the client to confirm it, not done ("Early ·
 * waiting on the client"), while the rest is open, and, confirmed, done on an override, an open exception until the rest
 * of its phase is done. A step a gate or an inspection not passed holds back, however far back (L-17 B, core
 * `gateHolds`), reads Held ahead of Ready, Next or Not started: it does not go under way until that is done.
 */
export function jobStanding(process: ProjectProcess, s: ProcessStepDto, ctx: StandingContext = {}): { standing: Standing; foot: string; deviation?: boolean } {
  const exception = Boolean((s.gate?.overrideReason && !s.gate.clearedAt) || (s.inspection?.overrideReason && !s.inspection.clearedAt));
  // A tile's day, with its year where the job runs over years or the day is not this year's (L-25a).
  const year: YearRule = { today: ctx.today, years: ctx.years };
  const day = (on: string | null) => dateWords(on, 'dayOrDate', year);
  const early = s.kind === 'gate' && s.deviation?.kind === 'early_gate' ? s.deviation : null;
  if (s.state === 'done') {
    if (exception || (early?.answer === 'confirmed' && early.open)) return { standing: 'override', foot: s.kind === 'inspection' ? 'Passed on an override' : 'Done on an override' };
    // A gate says who approved it, or that it is done (study 09, gates A): who marked it is on its panel.
    if (s.kind === 'gate') return { standing: 'done', foot: gateDone(s.gate?.passed, s.doneOn, year) };
    const word = s.kind === 'milestone' ? 'Reached' : s.kind === 'decision' ? 'Decided' : s.kind === 'inspection' ? 'Passed' : 'Done';
    return { standing: 'done', foot: `${word} · ${day(s.doneOn)}` };
  }
  if (s.state === 'in_progress' && s.kind === 'inspection') return { standing: 'failed', foot: 'Did not pass' };
  const held = unconfirmed(s);
  // A gate or an inspection not passed holds it back, however far back (L-17 B).
  const gated = (ctx.held?.get(s.id)?.length ?? 0) > 0;
  // Marked done before the rest of its phase: not done until the client confirms it, while the rest is still open. Once
  // the rest is done it stands as any gate does (it can be marked done as one), the proposal moot.
  const phaseOpen = ctx.holds ? (ctx.holds.get(s.id)?.length ?? 0) > 0 : true;
  if (early && held && phaseOpen) return { standing: 'ahead', foot: `Early · ${held}`, deviation: true };
  if (s.late) {
    // Come late: held until the client confirms it; confirmed, it stands as any step does, marked late.
    if (held && s.deviation?.kind === 'late') return { standing: 'ahead', foot: `Late · ${held}`, deviation: true };
    if (s.state === 'in_progress') return { standing: 'now', foot: 'Late · under way', deviation: true };
    const from = ctx.phaseName?.(s.late.from) ?? s.late.from;
    return { standing: isReady(process, s.id) && !ctx.holds?.get(s.id)?.length && !gated ? 'next' : 'ahead', foot: `Late · from ${from}`, deviation: true };
  }
  if (s.state === 'in_progress') return { standing: 'now', foot: s.startedOn ? `Under way · ${day(s.startedOn)}` : 'Under way' };
  // Held, ahead of Ready or Next, and of a plan: it does not go under way until what holds it is passed (its hover names it).
  if (gated) return { standing: 'ahead', foot: 'Held' };
  // The gate that closes a phase waits on the rest of its phase, by rule, with no line drawn for it.
  if (isReady(process, s.id) && !ctx.holds?.get(s.id)?.length) {
    return { standing: 'next', foot: s.kind === 'gate' && s.owner === 'client' ? 'Waiting on the client' : s.kind === 'gate' ? 'Next' : 'Ready' };
  }
  return { standing: 'ahead', foot: s.plan ? `Planned ${day(s.plan.on)}` : 'Not started' };
}

/** The phases a job is drawn in: the tree's, as it names them (core `phasesOf`), each with its facts. */
export function jobPhases(model: ProcessModel, steps: readonly TileStep[], titleOf: (key: string) => string | null): TilePhase[] {
  const named = phasesOf(model.dto.tree);
  const closers = new Map((model.dto.tree.phases ?? []).map((p) => [p.key as string, p.closedBy ?? null]));
  const idOfKey = new Map(model.dto.steps.filter((s) => !s.off && s.key && !s.parentId).map((s) => [s.key!, s.id]));
  return named.map((p) => {
    // Its steps: a task done early is its step's, never one of its phase's.
    const inIt = steps.filter((s) => s.phase === p.key && !s.end && !s.ghost && !s.early);
    // Done by the job's own record, so the count holds where the tiles draw no standing (setting it up).
    const done = inIt.filter((s) => model.dto.steps.some((x) => x.id === s.key && x.state === 'done')).length;
    const closerKey = closers.get(p.key) ?? null;
    const closer = closerKey ? (idOfKey.get(closerKey) ?? null) : null;
    const closerTitle = closer ? titleOf(closer) : null;
    return {
      key: p.key,
      name: p.name,
      what: p.what,
      closedBy: closer,
      facts: [`${inIt.length} ${inIt.length === 1 ? 'step' : 'steps'}`, inIt.length ? `${done} done` : '', closerTitle ? `ends with ${titleCase(closerTitle)}` : ''].filter(Boolean),
    };
  });
}

/**
 * What a tile carries at its foot setting a job up (P-14, Jason, 2026-10-09: "when you add a node, you should still have
 * the option to file it, you shouldn't have to open the node to remove it from the tree. so you either add it or file it
 * from the node, file has the title crossed off, clicking that again returns to a add/file node and an added node can be
 * filed to remove it from the tree inclusion"): one rule for every step of the job on the page. A step not on the job,
 * or left out of it (its dot), is added or put back from its phase's list or Find, never from the drawing: "no + on the
 * board" (design study 31, slice 2).
 *
 * - On the job: the filing box alone, which turns it off (`turn-off`, its own list going with it, as Turn it off on its
 *   panel does), and it is a dot, filed, offering + Add again; where it cannot go (a gate passed, a step done or under
 *   way, core `planDisable`) the box is held, saying why. Never the start or the end, Project Complete, the special last
 *   step that comes with Closeout or Handover (core `withEnd`).
 *
 * Kept as it ended, nothing.
 */
export interface SetupAction {
  /** What the filing box does: turns the step off, filing it away. */
  file: { kind: 'turn-off'; stepId: string } | null;
  /** Why the filing box is held, in words. */
  blocked?: string;
}

export function setupAction(model: ProcessModel, key: string): SetupAction | null {
  if (model.dto.kept) return null;
  const s = model.step(key);
  if (!s || s.off || s.parentId) return null;
  const start = model.dto.tree.start ?? null;
  if (s.key !== null && (s.key === start || s.key === BUILDER_END)) return null;
  const history = new Set(model.dto.steps.filter((x) => x.hasHistory).map((x) => x.id));
  const plan = planDisable(model.process, s.id, (id) => history.has(id));
  return plan.blocked ? { file: null, blocked: plan.blocked } : { file: { kind: 'turn-off', stepId: s.id } };
}

/** The key a step of the tree not on the job has where Find lists it. */
export const offeredKey = (key: string) => `next:${key}`;

/** The key a step left out of the job has in the drawing, its dot. */
export const leftOutKey = (key: string) => `out:${key}`;

/**
 * How a job is drawn (Jason, 2026-10-07: "in setup youd want to see all the nodes ungreeyed and full, but the build level
 * process that the client and admin/gc sees should have the job state"): setting its process up, every step full with no
 * state, and its steps left out as dots; `kept`, a job kept as it ended (L-19 A), its steps alone, full, no dots.
 * Running it, its steps alone, each with where it stands. A step come late is marked late either way. Nothing the job has
 * not got is drawn: it is added from its phase's list (design study 31, slice 2).
 */
export type JobMode = { setup: true; kept?: boolean } | { setup: false };

/**
 * A job's tile: a step's, with what holds it back named for its hover (`TileStep.hold`, L-17 B).
 */
export type JobTile = TileStep;

/**
 * What holds a step back, named, for its tile's hover and its words (L-17 B): "Held: Construction Authorized, two steps
 * back, is not done yet: it waits on the client." In the words its panel says it (holdWords `heldWords`); null when
 * nothing holds it.
 */
export function heldLine(model: ProcessModel, s: ProcessStepDto): string | null {
  const w = heldWords(model, s);
  return w ? `Held: ${w.line}` : null;
}

/** The band of a step of the tree the job has not got, as its tile would draw it: by who does it in the tree (bands.ts). */
export function treeStepBand(model: ProcessModel, key: string, tradeName: (key: string) => string | null = () => null): TileStep['band'] {
  const node = model.tree.node(key)!;
  return bandOf(jobFacts({ kind: node.kind, owner: node.owner ?? null, trade: node.trade ?? null, performer: null, key }, model.dto.tree.start ?? null), 'nsb', tradeName);
}

/** A job's steps as tiles, and, setting it up, its steps left out as dots where they would have been. */
export function jobTiles(model: ProcessModel, pictures: ReadonlyMap<string, string>, tradeName: (key: string) => string | null, mode: JobMode): JobTile[] {
  const start = model.dto.tree.start ?? null;
  // A step whose master node is not configured is marked as the master marks it (Jason, 2026-10-07: "clearly highlight
  // unconfigured steps in any tree"), read from the tree the job reads by the process tree page's rule.
  const completeness = lockedCompleteness(model.dto.tree);
  const unconfigured = (key: string | null): boolean => key !== null && completeness.get(key)?.configured === false;
  const phaseName = (k: string) => phasesOf(model.dto.tree).find((p) => p.key === k)?.name ?? k;
  const live = model.dto.steps.filter((s) => !s.off);
  const byId = new Map(live.map((s) => [s.id, s]));
  const tops = live.filter((s) => !s.parentId || !byId.has(s.parentId));
  // A task done in an earlier phase than its step (design study 31, slice 4; core `earlyTasks`) is drawn in that phase,
  // the outermost of them: one inside another is in its list.
  const early = earlyTasks(model.dto.tree, model.process);
  const drawnEarly = live.filter((s) => early.has(s.id) && !early.has(s.parentId ?? ''));
  const isDrawnEarly = new Set(drawnEarly.map((s) => s.id));
  /** Where a step is drawn: itself at the top or done early, else in the step it is in. */
  const drawnOf = (id: string): string => {
    let s = byId.get(id);
    for (let i = 0; s?.parentId && !isDrawnEarly.has(s.id) && byId.has(s.parentId) && i < 100; i++) s = byId.get(s.parentId);
    return s?.id ?? id;
  };
  // A task's wait is its step's, but a task done early keeps its own; its step waits on it in its place.
  const needs = new Map<string, Set<string>>();
  const need = (a: string, b: string) => {
    if (a !== b) needs.set(a, new Set([...(needs.get(a) ?? []), b]));
  };
  for (const l of model.dto.links) {
    if (l.kind !== 'requires' || !byId.has(l.stepId) || !byId.has(l.needsId)) continue;
    need(drawnOf(l.stepId), drawnOf(l.needsId));
  }
  for (const s of drawnEarly) need(early.get(s.id)!.stepId, s.id);
  // With no wait of its own, it hangs from the gate that opens its phase (`opensFrom`), as the engine holds it.
  const idOfKey = new Map(tops.filter((s) => s.key && !s.instance).map((s) => [s.key!, s.id]));
  const closers = phasesOf(model.dto.tree).map((p) => ({ key: p.key as string, closedBy: p.closedBy ? (idOfKey.get(p.closedBy) ?? null) : null }));
  const startId = start ? (idOfKey.get(start) ?? null) : null;
  for (const s of drawnEarly) {
    const from = needs.get(s.id)?.size ? null : opensFrom(closers, s.phase, startId);
    if (from) need(s.id, from);
  }
  // Its days with their year where the job runs over years (L-25a), read from what NSB's and the client's drawings both have.
  const year = yearRule(model.dto.today, live.map((s) => s.doneOn));
  /**
   * A task done early as its small tile (design study 31, §7): its own band (its step's trade where it has none, a sub's
   * share of that trade), its own picture else its step's, its step named under its name ("For Engineering"), and where
   * it stands at its foot in words as any tile's ("Under way · Oct 5", "Held"), never its ring's colour alone.
   */
  const earlyTile = (s: ProcessStepDto): JobTile => {
    const step = byId.get(early.get(s.id)!.stepId)!;
    const band = bandOf(jobFacts({ ...s, trade: s.trade ?? step.trade }, start), 'nsb', tradeName);
    const { standing, foot } = jobStanding(model.process, s, { holds: model.holds, held: model.held, phaseName, ...year });
    const hold = foot === 'Held' ? heldLine(model, s) : null;
    const name = stepName(step.title);
    return {
      key: s.id,
      phase: s.phase,
      needs: [...(needs.get(s.id) ?? [])],
      title: s.title,
      band,
      picture: (s.key && pictures.get(s.key)) || (step.key && pictures.get(step.key)) || null,
      standing,
      foot,
      early: { stepKey: step.id, stepName: name },
      ...(hold ? { hold } : {}),
      words: tileWords(s.title, band, earlyFor(name), hold ? hold.replace(/\.$/, '') : foot),
    };
  };
  const steps: JobTile[] = tops.map((s) => {
    const band = bandOf(jobFacts(s, start), 'nsb', tradeName);
    const { standing, foot, deviation } = jobStanding(model.process, s, { holds: model.holds, held: model.held, phaseName, ...year });
    // Held (L-17 B): what holds it, named on hover and in its words.
    const hold = foot === 'Held' ? heldLine(model, s) : null;
    // Or a special task it carries without what it needs set (spec "Pattern Mapping" §3.3): what is being built, until the
    // step is done, and never on a job kept as it ended, where nothing can be set any more.
    const notSet = unconfigured(s.key) || (!model.dto.kept && s.state !== 'done' && (s.specials ?? []).some((x) => !x.set));
    const end = s.key !== null && s.key === start ? ('start' as const) : s.key === 'project.archive' ? ('end' as const) : undefined;
    return {
      key: s.id,
      phase: s.phase,
      needs: [...(needs.get(s.id) ?? [])],
      ...(end ? { end } : {}),
      title: s.title,
      band,
      picture: (s.key && pictures.get(s.key)) || null,
      standing,
      foot,
      ...(deviation ? { deviation } : {}),
      // Sitting outside its own phase, come late: its lines back to an earlier phase cross the transitions (transitions.ts).
      ...(s.late ? { late: true } : {}),
      ...(notSet ? { configured: false } : {}),
      ...(hold ? { hold } : {}),
      words: tileWords(s.title, band, hold ? hold.replace(/\.$/, '') : foot, ...(notSet ? [NOT_CONFIGURED] : [])),
    };
  });
  steps.push(...drawnEarly.map(earlyTile));
  /** A step of the job as setting it up draws it: full, with no state; one come late and not done keeps its mark. */
  const setUp = (t: JobTile) => {
    const late = byId.get(t.key)?.late && byId.get(t.key)?.state !== 'done' ? `Late · from ${phaseName(byId.get(t.key)!.late!.from)}` : null;
    t.full = true;
    t.standing = null;
    delete t.hold;
    if (t.early) {
      // Done early, no standing at its foot, as any step setting the job up; its step still named under its name.
      delete t.foot;
      t.words = tileWords(t.title, t.band, earlyFor(t.early.stepName));
      return;
    }
    if (late) {
      t.foot = late;
      t.deviation = true;
    } else {
      delete t.foot;
      delete t.deviation;
    }
    t.words = tileWords(t.title, t.band, ...(t.foot ? [t.foot] : []), ...(t.configured === false ? [NOT_CONFIGURED] : []));
  };
  if (!mode.setup) return steps;
  for (const t of steps) setUp(t);
  // Kept as it ended (L-19 A): what the job has, full, and no dots.
  if (mode.kept) return steps;
  // A step left out: a dot where it would have been, under the steps of the job it would have come after, the nearest
  // back through the tree passing over what the job does not have (`anchors`: what came after it is rewired off them onto
  // the dot, below), then hung from what is drawn of them.
  const comesAfter = model.tree.beforeOf;
  const anchors = new Map<string, string[]>();
  const byKey = new Map(steps.map((t) => [t.key, t]));
  for (const key of model.dto.out) {
    const node = model.tree.node(key);
    if (!node || node.parent) continue;
    const after = new Set<string>();
    const seen = new Set<string>();
    let back = comesAfter.get(key) ?? [];
    while (back.length && !after.size) {
      const further: string[] = [];
      for (const k of back) {
        if (seen.has(k)) continue;
        seen.add(k);
        const id = idOfKey.get(k);
        if (id) after.add(id);
        else further.push(...(comesAfter.get(k) ?? []));
      }
      back = further;
    }
    if (!after.size) continue;
    anchors.set(leftOutKey(key), [...after]);
    const dot: JobTile = {
      key: leftOutKey(key),
      phase: phaseOfNode(model.dto.tree, key),
      needs: [...after],
      title: node.title,
      band: treeStepBand(model, key, tradeName),
      picture: pictures.get(key) ?? null,
      ghost: true,
      dot: true,
      foot: 'Left out',
      words: `${titleCase(node.title)}: left out of this job: tick it in its phase’s list to put it back.`,
    };
    steps.push(dot);
    byKey.set(dot.key, dot);
  }
  /** What is drawn of the steps a step of the tree comes after: the nearest back through the tree, a step of the job or a dot. */
  const drawnBefore = (key: string): string[] => {
    const hit = new Set<string>();
    const seen = new Set<string>([key]);
    let back = comesAfter.get(key) ?? [];
    while (back.length && !hit.size) {
      const further: string[] = [];
      for (const k of back) {
        if (seen.has(k)) continue;
        seen.add(k);
        const at = idOfKey.get(k) ?? (byKey.has(leftOutKey(k)) ? leftOutKey(k) : null);
        if (at) hit.add(at);
        else further.push(...(comesAfter.get(k) ?? []));
      }
      back = further;
    }
    return [...hit];
  };
  // A dot hangs from what is drawn of the steps it would have come after (a step of the job or another dot).
  for (const dot of anchors.keys()) byKey.get(dot)!.needs = drawnBefore(dot.slice(4));
  // The dot sits in the line (Jason, 2026-10-07: "the dot in the arrow and the crossed out step not a separate branch,
  // not an arrow to nowhere"): what came after the step left out, a step of the job or another dot, comes after its dot
  // in place of what the dot comes after, so the way runs on through it.
  const nodeOf = new Map<string, string>();
  for (const s of tops) if (s.key) nodeOf.set(s.id, s.key);
  const dots = steps.filter((s) => s.dot);
  for (const d of dots) nodeOf.set(d.key, d.key.slice(4));
  for (const d of dots) {
    const next = new Set(model.tree.node(d.key.slice(4))?.before ?? []);
    const from = anchors.get(d.key) ?? [];
    for (const t of steps) {
      const k = nodeOf.get(t.key);
      if (!k || !next.has(k) || t === d || t.needs.includes(d.key)) continue;
      const kept = t.needs.filter((n) => !from.includes(n));
      // Not waiting on what the dot comes after: a dot after another hangs from it all the same.
      if (kept.length === t.needs.length && t.ghost) continue;
      t.needs = [...kept, d.key];
    }
  }
  // A dot with anything drawn after it: the line runs on through it.
  for (const d of dots) if (steps.some((t) => t.needs.includes(d.key))) d.through = true;
  return steps;
}

/**
 * What a step opened on a job's tree draws as its chain (FocusChain; study 12, open A): the job's own steps, all the
 * way back and all the way on, what waits on a dot read through it to what the step left out came after (the dots are
 * not drawn there).
 */
export function jobChain(steps: readonly TileStep[], focused: string | null): TileStep[] {
  const dotNeeds = new Map(steps.filter((s) => s.key.startsWith('out:')).map((s) => [s.key, s.needs]));
  // A dot opened stays; any other is read through.
  const through = (n: string) => (n === focused ? [n] : (dotNeeds.get(n) ?? [n]));
  return steps.filter((s) => !s.ghost || s.key === focused).map((s) => ({ ...s, needs: s.needs.flatMap(through) }));
}

/**
 * What Find lists on a job (Jason, 2026-10-08, L-18 C: "Find a step finds every step of the tree"). Setting it up (`add`
 * given): every step the job has, and every step of the tree it could have (never the start, the end, which comes with
 * Closeout or Handover, a step's own list or a step added by name), each with its phase and where it stands, in the tree's order: on this job, left out, or not on
 * this job, and late where its phase has closed; one not on the job is added from the list ("+ Add"), one left out put
 * back ("Put it back"), each landing where it goes (late, with the reason asked). Running it, or kept as it ended, its
 * steps alone, each with where it stands.
 */
export function jobFindSteps(model: ProcessModel, tiles: readonly JobTile[], add: ((key: string) => void) | null): FindStep[] {
  const phaseName = (k: string | null) => (k ? (phasesOf(model.dto.tree).find((p) => p.key === k)?.name ?? null) : null);
  const order = (key: string | null) => (key ? (model.tree.order.get(key) ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER);
  // A task done early is found as its step, never apart from it.
  const mine = tiles.filter((t) => !t.ghost && !t.dot && !t.early);
  const onJob = mine.map((t) => {
    const s = model.step(t.key);
    const where = [phaseName(t.phase), add ? 'on this job' : (t.foot ?? null)].filter(Boolean).join(' · ');
    return { at: order(s?.key ?? null), step: { key: t.key, title: t.title, ...(where ? { where } : {}) } satisfies FindStep };
  });
  if (!add) return onJob.sort((a, b) => a.at - b.at).map((x) => x.step);
  const has = new Set(model.dto.steps.filter((s) => !s.off && s.key && !s.instance).map((s) => s.key!));
  const out = new Set(model.dto.out);
  const tree = model.dto.tree.nodes
    .filter((n) => n.parent === undefined && !n.repeatable && n.key !== model.dto.tree.start && n.key !== BUILDER_END && !has.has(n.key) && availableTo(n, model.process.kind))
    .map((n) => {
      const filed = out.has(n.key);
      const late = lateWords(model, n.key);
      const where = [phaseName(phaseOfNode(model.dto.tree, n.key)), filed ? 'left out' : 'not on this job', late ? `late, into ${late.into}` : null].filter(Boolean).join(' · ');
      const step: FindStep = { key: filed ? leftOutKey(n.key) : offeredKey(n.key), title: n.title, where, act: { word: filed ? 'Put it back' : '+ Add', run: () => add(n.key) } };
      return { at: order(n.key), step };
    });
  return [...onJob, ...tree].sort((a, b) => a.at - b.at).map((x) => x.step);
}
